import { getOrCreateMainLogger } from "../logger/main";
import defaultWindowOptions from "../permissions/defaultWindowOptions";
import { accessDenied } from "../util";
import { windowsIndex } from "./mainStore";
import { mainBus } from "./MainBus";
import { MainWindow } from "./MainWindow";
import { MainProcessBusEvent } from "../types/types";
import { get as _get } from "lodash";

const logger = getOrCreateMainLogger();

/**
 * Check if passed in value is a boolean
 * @param {boolean} val
 */
const isBoolean = (val: any) => typeof val === "boolean";

class PermissionsManager {
	systemPermissions: any;

	constructor() {
		this.addRestrictedListener = this.addRestrictedListener.bind(this);
		this.setDefaultPermissions = this.setDefaultPermissions.bind(this);
		this.reconcilePermissions = this.reconcilePermissions.bind(this);
	}

	/**
	 * Combine the current permissions set with a set of requested permissions
	 * Requested permissions can be a subset of the current permissions, but current permissions must always contain the entire set of allowed permissions for the system.
	 *
	 * The final permissions set will be based on the following rules:
	 * - If the currentPermissions (super set) is missing, the current permissions will be set to a default set of permissions before trying to combine.
	 * - Any requestedPermissions will only replace a value in the currentPermissions list if all of the below are true:
	 * 	-- The requested permission is an exact category and name match of a current permission
	 *  -- The requested permission is false when the current permission is true (because permissions are only changed to be more restrictive than their parent)
	 * @param {Object} currentPermissions //must include all possible permissions
	 * @param {Object} requestedPermissions //may be a subset of all possible permissions
	 */
	reconcilePermissions(currentPermissions: any, requestedPermissions: any) {
		// We've chosen not to do any logging around reconcile permissions because it's too large even for verbose
		if (!currentPermissions) {
			logger.warn("Initial list of permissions missing, setting to default list");
			currentPermissions = this.systemPermissions || defaultWindowOptions.permissions;
		}
		const finalPermissions = JSON.parse(JSON.stringify(currentPermissions));
		if (!requestedPermissions) return finalPermissions;
		// Iterate over requestedPermissions to check if any match a permission in the currentPermission super set
		// If so, set the final permission to the more restrictive of the two.
		Object.keys(requestedPermissions).forEach((category) => {
			Object.keys(requestedPermissions[category]).forEach((name) => {
				if (!(category in currentPermissions) || !(name in currentPermissions[category])) return;
				// Only change permissions if the requested component permissions are more restrictive
				// This will only happen if the current permission is true and the requested permission is false
				const permission = requestedPermissions[category][name];
				if (isBoolean(permission) && currentPermissions[category][name] - Number(permission) === 1) {
					finalPermissions[category][name] = permission;
				}
				// If the inner item is an object instead of a boolean iterate over it and do the same replacement as above
				if (typeof permission === "object" && permission !== null) {
					// rename for readablity, may need better index names...
					const requestedInnerCategory = permission;
					const currentInnerCategory = currentPermissions[category][name];
					const finalInnerCategory = finalPermissions[category][name];

					Object.keys(requestedInnerCategory).forEach((categoryName) => {
						if (
							categoryName in currentInnerCategory &&
							isBoolean(requestedInnerCategory[categoryName]) &&
							currentInnerCategory[categoryName] - Number(requestedInnerCategory[categoryName]) === 1
						) {
							finalInnerCategory[categoryName] = requestedInnerCategory[categoryName];
						}
					});
				}
			});
		});
		return finalPermissions;
	}

	/**
	 * Set Default Permissions on the PermissionsManager to be used by all components.
	 * Initial default permissions exist in defaultWindowOptions.js. This list is all possible permissions, while other permissions sets may be a subset.
	 * All uses of permissions assume the list is complete so reading in defaultWindowOptions.js is important
	 *
	 * If manifest permissions exist then Default permissions are a combination of these permissions and the initial default permissions.
	 * Otherwise they will be only the default permissions
	 * @param {Object} manifestPermissions
	 */
	setDefaultPermissions(manifestPermissions: any) {
		this.systemPermissions = this.reconcilePermissions(defaultWindowOptions.permissions, manifestPermissions);
	}

	/**
	 * Checks passed in permission to see if it's allowed
	 * If so, return true. If not allowed, return false.
	 *  * Refer to defaultWindowOptions.js for a list of available permissions
	 *
	 * This function will pass a permission check unless it's explicity false.
	 * This is because we don't want Finsemble to break if a permission is missing from the main list, or if an object has been removed (such as on shutdown)
	 * In the case of a missing permission, log it so it can be added to defaultWindowOptions.js later.
	 * We do the same if the object to look in is empty or missing a permissions section. This can happen on shutdown as windows close, but may also be an error.
	 * @param {MainWindow} mainWindow
	 * @param {string} permission (format: Category.permission, e.g System.clearCache)
	 */
	checkPermission(mainWindow: MainWindow | undefined, permission: string) {
		let result;
		if (mainWindow && mainWindow.permissions) {
			// lodash.get approved for permission checks for dynamic paths
			result = _get(mainWindow.permissions, permission);
		} else {
			logger.debug(
				`PermissionsManager->checkPermission MainWindow does not exist or does not have a permissions attribute. MainWindow: ${mainWindow?.windowName} permission: ${permission}`
			);
			return true;
		}
		if (result === undefined) {
			logger.info(
				`PermissionsManager->checkPermission Permission is not defined in default permission list ${permission}`
			);
			return true;
		}
		return result;
	}

	/**
	 * Wrap EventEmitters addListener so we can prevent listeners from being created if not permitted for each function
	 * If adding the listener is permitted or no permission is requested continue to EventEmitter's addListener, otherwise send an accessDenied error
	 * @param {string} eventName
	 * @param {function} handler
	 * @param {string} permission
	 * @returns {function} The unsubscriber for the handler
	 */
	addRestrictedListener(
		eventName: string,
		handler: (event: MainProcessBusEvent) => void,
		permission: string | null = null
	): () => void {
		const modifiedHandler = async (e: any) => {
			const { mainWindow } = windowsIndex.byWebContentsId[e.sender.id] ?? {};
			if (permission && !this.checkPermission(mainWindow, permission)) {
				// Permission denied is already logged at the calling level, logging here only for debugging
				logger.debug(`PermissionsManager->addRestrictedListener Permission denied: ${permission}`);
				accessDenied(permission, e.respond);
				return;
			}
			// Handlers may be async or sync. If async then we must await, so that the mainBus
			// can catch any unhandled rejections
			const isAsync = handler.constructor.name === "AsyncFunction";
			isAsync ? await handler(e) : handler(e);
		};
		return mainBus.addHandler(eventName, modifiedHandler);
	}
}

export const permissionsManager = new PermissionsManager();
